import { AxiosError, AxiosResponse } from 'axios';
import { useEffect, useRef } from 'react';

import { useAuthenticatedQuery } from '@/hooks/auth/use-auth-query-hooks';

import { apiErrorLogger, apiInfoLogger } from '@/lib/monitoring/api-logger';
import * as Logger from '@/lib/monitoring/logger';
import { PIXEL_DESTINATIONS_QUERY_KEY } from '@/lib/query/constants/pixel-destinations-query-key-constants';
import { useAccountState } from '@/contexts/account/account-context';
import {
  DestinationsApiService,
} from '@/services/internal-api/destinations/destinations-api-service';
import type { PixelDestinationResponse } from '@/services/internal-api/destinations/types/destinations-api-service-types';

// Re-export for backward compatibility

export function useFetchPixelDestinations(shouldLoad = true) {
  const { accountId, pixelId } = useAccountState();
  const fetchStartTime = useRef<number>(0);
  const hasLoggedMount = useRef(false);

  const isEnabled =
    shouldLoad &&
    accountId !== undefined &&
    accountId !== null &&
    accountId !== '' &&
    pixelId !== undefined &&
    pixelId !== null &&
    pixelId !== '';

  const query = useAuthenticatedQuery(
    [PIXEL_DESTINATIONS_QUERY_KEY, accountId, pixelId],
    () => {
      fetchStartTime.current = performance.now();
      return DestinationsApiService.getDestinationForPixel(pixelId ?? '');
    },
    {
      useErrorBoundary: false,
      onSuccess: (res: AxiosResponse<PixelDestinationResponse>) => {
        const destinations = res?.data?.destinations ?? [];
        const durationMs = Math.round(performance.now() - fetchStartTime.current);
        Logger.debug('useFetchPixelDestinations success', {
          accountId,
          pixelId,
          destinationCount: destinations.length,
          destinationIds: destinations.map(d => d.id),
          durationMs,
        });
        apiInfoLogger(
          'Fetch Pixel Destinations request successfully',
          'useFetchPixelDestinations',
          res
        );
      },
      onError: (error: AxiosError) => {
        const durationMs = Math.round(performance.now() - fetchStartTime.current);
        Logger.debug('useFetchPixelDestinations error', {
          accountId,
          pixelId,
          errorMessage: error.message,
          errorStatus: error.response?.status,
          durationMs,
        });
        apiErrorLogger(
          'Fetch Pixel Destinations request failed',
          'useFetchPixelDestinations',
          error
        );
      },
      enabled: isEnabled,
      staleTime: 1000 * 30,
      cacheTime: 1000 * 60 * 2,
    }
  );

  // Log mount state for debugging
  useEffect(() => {
    if (!hasLoggedMount.current) {
      hasLoggedMount.current = true;
      Logger.debug('useFetchPixelDestinations mounted', {
        shouldLoad,
        accountId,
        pixelId,
        isEnabled,
        hasAccountId: accountId !== undefined && accountId !== null && accountId !== '',
        hasPixelId: pixelId !== undefined && pixelId !== null && pixelId !== '',
      });
    }
  }, [shouldLoad, accountId, pixelId, isEnabled]);

  // Log query state changes for debugging loading issues
  useEffect(() => {
    Logger.debug('useFetchPixelDestinations state', {
      accountId,
      pixelId,
      isEnabled,
      isLoading: query.isLoading,
      isFetching: query.isFetching,
      isSuccess: query.isSuccess,
      isError: query.isError,
      status: query.status,
      fetchStatus: query.fetchStatus,
    });
  }, [accountId, pixelId, isEnabled, query.isLoading, query.isFetching, query.isSuccess, query.isError, query.status, query.fetchStatus]);

  return query;
}
